import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Agent } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';

function readApiError(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message.trim()) return fallback;
  try {
    const body = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (Array.isArray(body.message) && body.message[0]) return body.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

export function AgentsPage() {
  const { workspaceId } = useApp();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [job, setJob] = useState('');
  const [pending, setPending] = useState<Agent | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function load() {
    const result = await apiClient.listAgents();
    setAgents(result.items);
  }

  useEffect(() => {
    void load().catch(() => setAgents([]));
  }, [workspaceId]);

  async function create() {
    if (!name.trim()) return;
    await apiClient.createAgent({
      name: name.trim(),
      jobDescription: job,
      systemPrompt: '',
      status: 'draft',
      tier: 1,
      agentRole: 'specialist',
      knowledgeBaseIds: [],
    });
    setName('');
    setJob('');
    setCreating(false);
    await load();
  }

  async function remove(agent: Agent) {
    setDeleteError(null);
    try {
      await apiClient.deleteAgent(agent.id);
      setPending(null);
      await load();
    } catch (err) {
      setDeleteError(
        readApiError(err, 'Could not delete this agent. Agents with run history must be archived instead.'),
      );
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Agents</h1>
        <Button size="sm" onClick={() => setCreating((v) => !v)}>New agent</Button>
      </div>

      {creating && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="grid max-w-xl gap-3">
            <Input placeholder="Role name (e.g. Support Engineer)" value={name} onChange={(e) => setName(e.target.value)} />
            <Input placeholder="Job description" value={job} onChange={(e) => setJob(e.target.value)} />
            <Button className="w-fit" disabled={!name.trim()} onClick={() => void create()}>Create</Button>
          </div>
        </div>
      )}

      {deleteError && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {deleteError}
        </div>
      )}

      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
        {agents.map((a) => (
          <div key={a.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-start justify-between gap-2">
              <strong className="min-w-0 truncate">{a.name}</strong>
              <div className="flex shrink-0 items-center gap-1">
                <Badge variant={a.status === 'active' ? 'success' : 'warning'}>{a.status}</Badge>
                <Button size="sm" variant="ghost" className="text-red-600" title="Delete agent" onClick={() => setPending(a)}>
                  Delete
                </Button>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">{a.jobDescription || 'No description'}</p>
            <Button className="mt-3" size="sm" variant="ghost" onClick={() => navigate(`/agents/${a.id}`)}>
              Open
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete agent</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Delete agent "{pending?.name}"? This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!pending} onClick={() => pending && void remove(pending)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
